// Public, indexable hub for all free Grade9Ready resources.
// Sits under the new "Free Resources" nav item — also referenced from footer
// + the "internal-linking pass" at the bottom of every cheat sheet.

import { Link } from 'react-router-dom';
import {
  Calendar, Calculator, Sparkle, ArrowRight, BookOpen, Trophy, GraduationCap, Lightning,
} from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { trackEvent, EVT } from '../utils/analytics';

const SITE_URL = 'https://grade9ready.com';
const PATH = '/free-resources';

const RESOURCES = [
  {
    href: '/free-revision-planner', testid: 'free-resources-planner',
    icon: Calendar,    accent: 'indigo',
    title: 'Revision Planner',
    blurb: 'Pick your exam date + subjects → printable 8-week timetable with proven revision techniques.',
    badge: 'Printable',
  },
  {
    href: '/gcse-grade-boundaries-calculator', testid: 'free-resources-grade-calc',
    icon: Calculator,  accent: 'amber',
    title: 'Grade Boundaries Calculator',
    blurb: 'Enter raw marks → instant grade + marks-to-next-grade gap. AQA 2024 boundary data.',
    badge: 'Interactive',
  },
  {
    href: '/gcse-maths-examiner-tips', testid: 'free-resources-maths-tips',
    icon: BookOpen,    accent: 'indigo',
    title: 'Maths Examiner Tips',
    blurb: 'AQA examiner cheat sheet — why marks are lost, what they want, the Grade-9 move per topic.',
    badge: 'Cheat sheet',
  },
  {
    href: '/gcse-english-examiner-tips', testid: 'free-resources-english-tips',
    icon: BookOpen,    accent: 'rose',
    title: 'English Examiner Tips',
    blurb: 'Macbeth, ACC, Inspector Calls, Power & Conflict, Lang P1 + P2 — the moves examiners reward.',
    badge: 'Cheat sheet',
  },
  {
    href: '/gcse-science-examiner-tips', testid: 'free-resources-science-tips',
    icon: BookOpen,    accent: 'emerald',
    title: 'Science Examiner Tips',
    blurb: 'AQA Biology, Chemistry, Physics — Grade-9 moves on the highest-yield topics.',
    badge: 'Cheat sheet',
  },
  {
    href: '/free-lesson', testid: 'free-resources-free-lesson',
    icon: Sparkle,     accent: 'primary',
    title: 'Try a Free Lesson',
    blurb: 'Three GCSE questions, instant AI-style feedback. No signup. See exactly how Grade9Ready marks.',
    badge: 'Try now',
  },
];

const accent = {
  indigo:  'bg-indigo-100 text-indigo-700 border-indigo-200',
  rose:    'bg-rose-100 text-rose-700 border-rose-200',
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  amber:   'bg-amber-100 text-amber-700 border-amber-200',
  primary: 'bg-primary/10 text-primary border-primary/30',
};

export default function FreeResources() {
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Free Resources', item: `${SITE_URL}${PATH}` },
    ],
  };
  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Grade9Ready Free GCSE Resources',
    description: 'Free printable GCSE revision tools — planners, calculators, examiner cheat sheets and a free sample lesson.',
    inLanguage: 'en-GB',
  };

  return (
    <div className="min-h-screen bg-background" data-testid="free-resources-page">
      <SEO
        title="Free GCSE Resources — Planners, Cheat Sheets & a Sample Lesson"
        description="Free GCSE revision tools from Grade9Ready: printable planner, grade boundaries calculator, examiner cheat sheets for Maths/English/Science and a free sample lesson. No signup needed."
        path={PATH}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionJsonLd) }} />

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground font-bold">Free Resources</span>
      </nav>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 text-[11px] font-black uppercase tracking-wide mb-5">
          <Lightning size={12} weight="fill" /> 100% free · no signup needed
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black leading-[1.05] mb-4 max-w-4xl" data-testid="free-resources-h1">
          Free GCSE resources that actually move grades
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">
          Printable planners, grade calculators, examiner cheat sheets and a free sample lesson — all built from the same data Grade9Ready uses inside the platform.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="free-resources-grid">
          {RESOURCES.map((r) => {
            const Icon = r.icon;
            return (
              <Link
                key={r.href}
                to={r.href}
                onClick={() => trackEvent(EVT.CTA_CLICK, { cta: 'free_resource', resource: r.testid, location: 'free_resources_hub' })}
                data-testid={r.testid}
                className="neo-card p-5 hover:-translate-y-0.5 transition-all flex flex-col gap-3 group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${accent[r.accent]}`}>
                    <Icon size={20} weight="bold" />
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded border ${accent[r.accent]}`}>
                    {r.badge}
                  </span>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg mb-1">{r.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{r.blurb}</p>
                </div>
                <div className="mt-auto inline-flex items-center gap-1 text-sm font-bold text-primary group-hover:gap-2 transition-all">
                  Open <ArrowRight size={14} weight="bold" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="neo-card p-6 sm:p-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/30 flex flex-col md:flex-row items-start md:items-center gap-5 justify-between">
          <div>
            <h3 className="font-heading font-black text-2xl mb-1.5 flex items-center gap-2">
              <GraduationCap size={22} className="text-primary" /> Ready for the full platform?
            </h3>
            <p className="text-muted-foreground max-w-2xl">
              Every Grade9Ready topic includes AI-marked practice, instant WWW + EBI feedback, examiner-tip blocks and parent visibility. Start with a free trial — no card needed.
            </p>
          </div>
          <Link
            to="/signup"
            onClick={() => trackEvent(EVT.CTA_CLICK, { cta: 'start_free_trial', location: 'free_resources_footer' })}
            data-testid="free-resources-cta-trial"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-primary text-primary-foreground font-heading font-bold shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:translate-y-[1px] transition-transform shrink-0"
          >
            Create Parent Account <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
